import { useCallback, useEffect, useRef, useState } from 'react';

// Keyboard nav for "search input + scrollable option list". Arrow keys
// wrap at ends; Enter submits the highlighted row; Esc calls onClear.
// Caller owns the query — pass it via `resetSignal` to reset highlight
// (two distinct queries can have the same itemCount).

export interface KeyboardListApi {
  activeIndex: number;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  setActiveIndex: (i: number) => void;
  listRef: React.RefObject<HTMLDivElement | null>;
  isActive: (i: number) => boolean;
}

interface Options {
  itemCount: number;
  onSubmit: (index: number) => void;
  onClear?: () => void;
  resetSignal?: unknown;
}

export function useKeyboardList(opts: Options): KeyboardListApi {
  const { itemCount, onSubmit, onClear, resetSignal } = opts;
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveIndex(0);
  }, [resetSignal]);

  useEffect(() => {
    if (activeIndex >= itemCount) setActiveIndex(Math.max(0, itemCount - 1));
  }, [itemCount, activeIndex]);

  // Scroll active row into view.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const row = list.querySelector<HTMLElement>(
      `[data-kb-index="${activeIndex}"]`
    );
    if (!row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    const viewTop = list.scrollTop;
    const viewBottom = viewTop + list.clientHeight;
    if (top < viewTop) list.scrollTop = top;
    else if (bottom > viewBottom) list.scrollTop = bottom - list.clientHeight;
  }, [activeIndex]);

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (itemCount === 0) {
        if (e.key === 'Escape' && onClear) {
          e.preventDefault();
          onClear();
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % itemCount);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + itemCount) % itemCount);
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0 && activeIndex < itemCount) {
          e.preventDefault();
          onSubmit(activeIndex);
        }
      } else if (e.key === 'Escape') {
        if (onClear) {
          e.preventDefault();
          onClear();
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveIndex(itemCount - 1);
      }
    },
    [itemCount, activeIndex, onSubmit, onClear]
  );

  const isActive = useCallback((i: number) => i === activeIndex, [activeIndex]);

  return { activeIndex, onInputKeyDown, setActiveIndex, listRef, isActive };
}
